/* eslint-disable @typescript-eslint/naming-convention, camelcase, no-underscore-dangle -- OpenSearch DSL keys, `_source` and log field names are not camelCase */
import type { opensearchtypes } from '@opensearch-project/opensearch';
import { isNumber } from 'lodash';
import { IBuildSearchPayload } from '@logz-pkg/opensearch-dsl';
import { getShardFailureMessage } from '../opensearch/opensearch-search-error';
import {
  DEPLOYMENT_EVENTS_LOG_TYPE,
  DEPLOYMENT_EVENTS_MAX_PAGES,
  DEPLOYMENT_EVENTS_PAGE_SIZE,
  DEPLOYMENT_EVENT_SOURCE_FIELDS,
} from './constants';
import type {
  DeploymentEventHit,
  DeploymentEventSource,
  DeploymentEventsAfterKey,
  DeploymentEventsAggregations,
  DeploymentEventsPage,
  DeploymentEventsResult,
  FetchDeploymentEventPayload,
  SearchDeploymentEvents,
} from './types';

const toPhraseList = (values: string[]): string =>
  values.map(value => `"${value.replace(/(["\\])/g, '\\$1')}"`).join(' OR ');

const buildQueryString = ({ serviceName, environments }: FetchDeploymentEventPayload): string => {
  const clauses = [`type:${DEPLOYMENT_EVENTS_LOG_TYPE}`, 'eventType:MODIFIED'];

  if (serviceName.length > 0) clauses.push(`newObject.metadata.name:(${toPhraseList(serviceName)})`);

  if (environments && environments.length > 0) clauses.push(`env_id:(${toPhraseList(environments)})`);

  return clauses.join(' AND ');
};

/**
 * A rollout is the one document per (object, generation) whose `oldObject` still carries the previous
 * generation; every later document with that generation is status churn. Grouping by that pair and taking
 * the document that sorts first puts the rollout at hand without comparing two fields of one document —
 * Painless scripts are disabled on the cluster.
 *
 * `composite` ENUMERATES the pairs rather than ranking them. Nested `terms` ranked by doc_count (its
 * default), which handed the slots to the objects with the most status churn and pushed out a workload
 * whose only event was the rollout we were looking for; it also multiplied its two caps into ~50k possible
 * buckets, near enough `search.max_buckets` to fail a whole search outright. Composite bounds the buckets
 * by `size` and makes what was dropped visible and resumable through `after_key`.
 *
 * The sort needs BOTH guards, and they cover different things. `missing: _last` keeps ADDED-style documents
 * with no `oldObject` from winning a bucket on an index that maps the field. `unmapped_type` covers the
 * index that does not map it at all: a field sort is resolved when the shard query is built, so an unmapped
 * shard throws `query_shard_exception: No mapping found for [...] in order to sort on` before reading a
 * single document, and the partial failure comes back inside an HTTP 200 that nothing was inspecting.
 */
const buildSearchPayload = ({
  after,
  ...payload
}: FetchDeploymentEventPayload & { after?: DeploymentEventsAfterKey }): IBuildSearchPayload => ({
  size: 0,
  trackTotalHits: false,
  query: {
    bool: {
      must: [{ query_string: { query: buildQueryString(payload) } }],
      filter: [
        {
          range: {
            '@timestamp': {
              gte: new Date(payload.from * 1000).toISOString(),
              lt: new Date(payload.to * 1000).toISOString(),
            },
          },
        },
      ],
    },
  },
  aggs: {
    rollouts: {
      composite: {
        size: DEPLOYMENT_EVENTS_PAGE_SIZE,
        // A document missing either source field is skipped rather than bucketed, which is the filter we want.
        sources: [
          { uid: { terms: { field: 'newObject.metadata.uid' } } },
          { generation: { terms: { field: 'newObject.metadata.generation', order: 'desc' } } },
        ],
        ...(after ? { after } : {}),
      },
      aggs: {
        changeEvent: {
          top_hits: {
            size: 1,
            sort: [
              { 'oldObject.metadata.generation': { order: 'asc', missing: '_last', unmapped_type: 'long' } },
              { '@timestamp': { order: 'asc' } },
            ],
            _source: DEPLOYMENT_EVENT_SOURCE_FIELDS,
          },
        },
      },
    },
  },
});

// A bucket whose rollout happened before the time range surfaces a churn document (equal generations) instead.
const isRolloutHit = (hit: opensearchtypes.SearchHit<DeploymentEventSource>): hit is DeploymentEventHit => {
  const newGeneration = hit._source?.newObject?.metadata?.generation;
  const oldGeneration = hit._source?.oldObject?.metadata?.generation;

  return isNumber(newGeneration) && isNumber(oldGeneration) && newGeneration > oldGeneration;
};

const toTimestamp = (hit: DeploymentEventHit): number => new Date(hit._source['@timestamp']).getTime();

const byNewestFirst = (a: DeploymentEventHit, b: DeploymentEventHit): number => toTimestamp(b) - toTimestamp(a);

/** The rollout documents one page of composite buckets collapsed to. */
const selectRolloutPage = (
  response: opensearchtypes.SearchResponse<DeploymentEventSource> | undefined,
): DeploymentEventsPage => {
  // The aggregation names are ours (see buildSearchPayload); the client types them as a generic bag.
  const { rollouts } = (response?.aggregations ?? {}) as Partial<DeploymentEventsAggregations>;
  const buckets = rollouts?.buckets ?? [];

  return {
    hits: buckets.flatMap(bucket => bucket.changeEvent.hits.hits).filter(isRolloutHit),
    afterKey: rollouts?.after_key,
    // OpenSearch returns `after_key` on the LAST page too, so its presence proves nothing. A short page is
    // the only reliable end-of-stream signal.
    hasMore: buckets.length === DEPLOYMENT_EVENTS_PAGE_SIZE,
  };
};

/** Every rollout in the window, newest first, walking composite pages up to the budget. */
const fetchRollouts = async ({
  payload,
  search,
}: {
  payload: FetchDeploymentEventPayload;
  search: SearchDeploymentEvents;
}): Promise<DeploymentEventsResult> => {
  const hits: DeploymentEventHit[] = [];
  let after: DeploymentEventsAfterKey | undefined;
  let shardFailure: string | undefined;

  for (let page = 0; page < DEPLOYMENT_EVENTS_MAX_PAGES; page += 1) {
    // Sequential by construction: each page resumes from the previous page's `after_key`.
    // eslint-disable-next-line no-await-in-loop
    const response = await search(buildSearchPayload({ ...payload, after }));

    if (!response && page === 0) {
      throw new Error('Deployment events search returned no response');
    }

    const { hits: pageHits, afterKey, hasMore } = selectRolloutPage(response);

    hits.push(...pageHits);
    shardFailure = shardFailure ?? getShardFailureMessage(response);

    if (!hasMore || !afterKey) {
      return { hits: hits.sort(byNewestFirst), isTruncated: false, shardFailure };
    }

    after = afterKey;
  }

  return { hits: hits.sort(byNewestFirst), isTruncated: true, shardFailure };
};

/**
 * Identity of what a fetch asks about, deliberately WITHOUT the time window: a consumer holds one entity's
 * markers and must be able to tell whether they describe the entity it is currently rendering. Including
 * `from`/`to` would blank the markers on every time-range change, which only widens the same question.
 */
const buildEntityKey = ({
  serviceName,
  environments,
}: Pick<FetchDeploymentEventPayload, 'serviceName' | 'environments'>): string =>
  JSON.stringify([[...serviceName].sort(), [...(environments ?? [])].sort()]);

export const k8sDeploymentEventsSearch = {
  buildSearchPayload,
  selectRolloutPage,
  fetchRollouts,
  buildEntityKey,
};
